
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { toast } from 'sonner';
import { useLanguage } from './useLanguage';

export interface Release {
  nazwa_projektu: string;
  wersja: string;
  data_wydania: string;
  url_projektu: string;
  url_zdjecia: string;
}

export function useReleases() {
  const [releases, setReleases] = useState<Release[]>([]);
  const [filteredReleases, setFilteredReleases] = useState<Release[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast: uiToast } = useToast();
  const { t } = useLanguage();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReleases, setTotalReleases] = useState(0);
  const itemsPerPage = 6; // Display 6 releases per page

  // Fetch releases data from Supabase storage
  useEffect(() => {
    const fetchReleases = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Get the releases.json file from Supabase storage
        const { data, error } = await supabase
          .storage
          .from('dane')
          .download('releases.json');
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (!data) {
          throw new Error('No data found');
        }

        // Parse the JSON data
        const text = await data.text();
        const json = JSON.parse(text) as Release[];
        
        // Sort releases by date (newest first)
        const sortedReleases = json.sort((a, b) => 
          new Date(b.data_wydania).getTime() - new Date(a.data_wydania).getTime()
        );
        
        setTotalReleases(sortedReleases.length);
        setReleases(sortedReleases);
        
        // Apply pagination to filtered results
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedReleases = sortedReleases.slice(startIndex, startIndex + itemsPerPage);
        
        setFilteredReleases(paginatedReleases);
      } catch (err) {
        console.error('Error fetching releases:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        uiToast({
          title: t('releases.error_title'),
          description: t('releases.error_description'),
          variant: 'destructive',
        });
        toast.error(t('releases.error_toast'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReleases();
  }, [t, uiToast, currentPage]);

  // Filter releases based on search query (minimum 3 characters)
  useEffect(() => {
    if (searchQuery.length >= 3) {
      const filtered = releases.filter(release =>
        release.nazwa_projektu.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      // Apply pagination to filtered results
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginatedFiltered = filtered.slice(startIndex, startIndex + itemsPerPage);
      
      setFilteredReleases(paginatedFiltered);
    } else if (releases.length > 0) {
      // If no search query, apply pagination to all releases
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginatedReleases = releases.slice(startIndex, startIndex + itemsPerPage);
      
      setFilteredReleases(paginatedReleases);
    }
  }, [searchQuery, releases, currentPage]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1); // Reset to first page
    
    // Apply pagination to all releases after clearing search
    if (releases.length > 0) {
      const paginatedReleases = releases.slice(0, itemsPerPage);
      setFilteredReleases(paginatedReleases);
    }
  };

  // Pagination controls
  const goToNextPage = () => {
    const maxPage = Math.ceil(
      searchQuery.length >= 3 
        ? releases.filter(r => r.nazwa_projektu.toLowerCase().includes(searchQuery.toLowerCase())).length / itemsPerPage
        : totalReleases / itemsPerPage
    );
    
    if (currentPage < maxPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  return {
    releases: filteredReleases,
    isLoading,
    error,
    searchQuery,
    handleSearchChange,
    clearSearch,
    totalReleases,
    filteredCount: searchQuery.length >= 3 
      ? releases.filter(r => r.nazwa_projektu.toLowerCase().includes(searchQuery.toLowerCase())).length 
      : totalReleases,
    currentPage,
    goToNextPage,
    goToPrevPage,
    hasNextPage: currentPage < Math.ceil(
      searchQuery.length >= 3 
        ? releases.filter(r => r.nazwa_projektu.toLowerCase().includes(searchQuery.toLowerCase())).length / itemsPerPage
        : totalReleases / itemsPerPage
    ),
    hasPrevPage: currentPage > 1,
    itemsPerPage
  };
}
